/**
 * Unified Agents + Evals lab.
 *
 * Left rail: list of agents (from `listEvalAgents`). Click one to focus it.
 * Main area: four tabs scoped to the focused agent
 *   - Overview  — metadata, last eval score, quick stats
 *   - Prompts   — all versions of this agent's identity prompt, editable,
 *                 with fork/save/delete. Also shows the shared templates.
 *   - Evals     — per-agent eval reports AND a Prompt × Model matrix with
 *                 per-cell run buttons. Each cell shows the most recent
 *                 eval score for (promptVersion, model).
 *   - Compare   — run the same test cases across multiple agents at once
 *                 and see a per-agent matrix side by side.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {usePageTitle} from '../../hooks/usePageTitle';
import {
  useListEvalAgents,
  useListAgentPrompts,
  useListOpenRouterModels,
  useRunEval,
  useRunMultiModelEval,
  useSaveAgentPrompt,
  useForkAgentPrompt,
  useDeleteAgentPrompt,
  useGetEvalReportsForAgent,
  useGetEvalReport,
  useGetEvalTestCases,
  useSeedEvalTestCases,
  useEvolveAgentPrompt
} from '../../api/graphql_evals';
import {useListBullmqJobs} from '../../api/graphql_failed_tasks';
import {useConfirm, useNotify} from '../ui/ConfirmDialog';
import {ModelPicker} from './ModelPicker';
import {usePersistedState} from '../../hooks/usePersistedState';
import {
  AgentSpecialization,
  AgentPromptEntry,
  AgentPromptKind,
  EvalAgentEntry,
  OpenRouterModelEntry,
  EvalReport,
  BullmqJobState,
  ScoreDirection
} from '../../types/generated/graphql';
import {
  humanizeSpecialization,
  shortSpecialization,
  humanizeAgentType,
  humanizeScoreDirection,
  humanizeDataRequirement,
  humanizeSummarizationNeed,
  resolveAgentThumbnailUrl,
  colorForString
} from './agentLabels';
import {
  pcolors,
  cardSx,
  pageSx,
  pageTitleSx,
  sectionLabelSx,
  scoreColors,
  scoreBadgeSx,
  tableHeadRowSx,
  tableHeadCellSx,
  tableBodyCellSx,
  listRowSx
} from '../../theme/planapusTheme';

// Reusable avatar widget: renders the PNG when one exists, falls back to a
// deterministic-colored initials bubble.
function AgentAvatar({
  agent,
  size = 40
}: {
  agent: Pick<EvalAgentEntry, 'name' | 'specialization' | 'thumbnails'>;
  size?: number;
}) {
  const src = resolveAgentThumbnailUrl(agent.thumbnails);
  const initials = (agent.name || agent.specialization)
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w.charAt(0))
    .join('')
    .toUpperCase();
  return (
    <Avatar
      src={src ?? undefined}
      sx={{
        width: size,
        height: size,
        bgcolor: colorForString(String(agent.specialization)),
        fontWeight: 600,
        fontSize: size * 0.4
      }}
    >
      {!src && initials}
    </Avatar>
  );
}

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

// Default matrix starting set when localStorage has no persisted selection.
// Picked to cover a useful price/capability spread:
//   - gemini-2.5-flash-lite : cheapest / fastest baseline
//   - gpt-oss-120b         : open-weights model for cost comparison
//   - claude-sonnet-4.6    : current Anthropic Sonnet
//   - gemini-2.5-flash     : step up from -lite on reasoning without jumping to pro
const STARRED_MODELS = [
  'google/gemini-2.5-flash-lite',
  'openai/gpt-oss-120b',
  'anthropic/claude-sonnet-4.6',
  'google/gemini-2.5-flash'
];

function formatScore(s: number | null | undefined): string {
  if (s == null) return '—';
  return `${(s * 100).toFixed(1)}%`;
}

function scoreColor(s: number | null | undefined): 'success' | 'warning' | 'error' | 'default' {
  if (s == null) return 'default';
  if (s > 0.85) return 'success';
  if (s > 0.65) return 'warning';
  return 'error';
}

// Agent identity prompts are keyed by `<id>_<version_underscored>.md`.
// The `id` lives on the .ts loader; for the UI we reconstruct a predictable
// agent.id from specialization + version. In goodshowhq all 4 built-in agents
// use the pattern `<specialization_lowercase>_agent_<ver_underscored>`.
function agentPromptId(spec: AgentSpecialization, version: string): string {
  const specId = String(spec).toLowerCase();
  const verId = version.replace(/\./g, '_');
  return `${specId}_agent_${verId}`;
}

// ----------------------------------------------------------------
// Overview tab
// ----------------------------------------------------------------
function OverviewTab({agent, client}: {agent: EvalAgentEntry; client: Props['client']}) {
  const {data: reportsData} = useGetEvalReportsForAgent(client, {
    agentSpec: agent.specialization,
    agentVersion: agent.version,
    limit: 10
  });
  const reports: EvalReport[] = reportsData?.getEvalReportsForAgent ?? [];
  const best = reports[0];

  return (
    <Paper sx={{...cardSx, p: 2.5}}>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 3}}>
        <Box sx={{display: 'flex', gap: 2}}>
          <AgentAvatar agent={agent} size={64} />
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography sx={sectionLabelSx}>Agent</Typography>
            <Typography sx={{fontSize: 18, fontWeight: 600, color: pcolors.textPrimary, mt: 0.5}}>
              {agent.name}
            </Typography>
            <Typography sx={{fontSize: 12, color: pcolors.textSecondary}}>
              {humanizeSpecialization(agent.specialization)} · v{agent.version}
            </Typography>
            {agent.description && (
              <Typography sx={{fontSize: 13, color: pcolors.textPrimary, mt: 1.5, lineHeight: 1.55}}>
                {agent.description}
              </Typography>
            )}
            <Box sx={{mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
              <Chip
                size="small"
                label={humanizeAgentType(agent.type)}
                sx={{fontSize: 11, fontWeight: 600, bgcolor: pcolors.blue[50], color: pcolors.blue[700]}}
              />
              <Chip
                size="small"
                label={humanizeScoreDirection(agent.scoreDirection)}
                sx={{fontSize: 11, bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
              />
              <Chip
                size="small"
                label={`Ages ${agent.min_age}–${agent.max_age}`}
                sx={{fontSize: 11, bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
              />
              <Tooltip
                title={
                  agent.dataRequirement === 'TRANSCRIPT_REQUIRED'
                    ? 'Needs the video transcript to run. Skipped on videos with no captions.'
                    : agent.dataRequirement === 'SUMMARY_REQUIRED'
                      ? 'Needs the upstream summary. Metadata alone is not enough.'
                      : 'Runs on title + channel + description. No transcript needed.'
                }
              >
                <Chip
                  size="small"
                  label={`Needs: ${humanizeDataRequirement(agent.dataRequirement)}`}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor:
                      agent.dataRequirement === 'TRANSCRIPT_REQUIRED'
                        ? pcolors.amber[50]
                        : agent.dataRequirement === 'SUMMARY_REQUIRED'
                          ? pcolors.blue[50]
                          : pcolors.green[50],
                    color:
                      agent.dataRequirement === 'TRANSCRIPT_REQUIRED'
                        ? pcolors.amber[700]
                        : agent.dataRequirement === 'SUMMARY_REQUIRED'
                          ? pcolors.blue[700]
                          : pcolors.green[700],
                  }}
                />
              </Tooltip>
              {agent.summarizationNeed && (
                <Tooltip title="How the upstream summarizer prepares the video input for this agent.">
                  <Chip
                    size="small"
                    label={humanizeSummarizationNeed(agent.summarizationNeed)}
                    sx={{fontSize: 11, bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography sx={sectionLabelSx}>Recent eval</Typography>
          {reports.length === 0 ? (
            <Typography sx={{mt: 1, fontSize: 13, color: pcolors.textSecondary}}>
              No eval runs yet. Go to the Evals tab and click a cell in the matrix to queue one.
            </Typography>
          ) : (
            <Box sx={{mt: 1}}>
              <Box sx={scoreBadgeSx(best.overallScore, ScoreDirection.HigherIsBetter)}>
                {formatScore(best.overallScore)}
              </Box>
              <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 1, fontFamily: 'monospace'}}>
                {best.model}
              </Typography>
              <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 0.25}}>
                {best.passedCases}/{best.totalCases} passed · {new Date(best.ranAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

// ----------------------------------------------------------------
// Prompts tab — editor + version list
// ----------------------------------------------------------------
const COACH_MODELS = [
  'anthropic/claude-opus-4-5',
  'anthropic/claude-opus-4-1',
  'openai/gpt-5',
  'openai/gpt-5-mini',
  'google/gemini-2.5-pro'
];

function PromptsTab({agent, client}: {agent: EvalAgentEntry; client: Props['client']}) {
  const {data: promptsData, loading, refetch} = useListAgentPrompts(client);
  const {data: modelsData} = useListOpenRouterModels(client);
  const availableModels: OpenRouterModelEntry[] = modelsData?.listOpenRouterModels ?? [];
  const allPrompts: AgentPromptEntry[] = promptsData?.listAgentPrompts ?? [];
  const [evolveOpen, setEvolveOpen] = useState(false);
  const [evolvePrompt] = useEvolveAgentPrompt(client);
  // Evolve dialog selections are app-wide defaults (not agent-scoped) — you
  // typically want the same coach model across agents once you've picked one.
  const [coachModel, setCoachModel] = usePersistedState<string>(
    'agentsLab.evolve.coachModel',
    COACH_MODELS[0],
  );
  const [coachSample, setCoachSample] = usePersistedState<number>(
    'agentsLab.evolve.coachSample',
    20,
  );
  const [autoEvalModel, setAutoEvalModel] = usePersistedState<string>(
    'agentsLab.evolve.autoEvalModel',
    'openai/gpt-4o-mini',
  );
  const [autoEvalEnabled, setAutoEvalEnabled] = usePersistedState<boolean>(
    'agentsLab.evolve.autoEvalEnabled',
    true,
  );
  const [evolveBusy, setEvolveBusy] = useState(false);
  const [evolveMessage, setEvolveMessage] = useState<{
    severity: 'success' | 'error';
    text: string;
    jobId?: string | null;
  } | null>(null);

  // Identity prompts for this agent — match on the agent's real id, which
  // is the stable key prompts are stored under across versions. Don't
  // derive from specialization: a few agents (e.g. EDUCATION_LANGUAGE's
  // `language_development_agent_1_0_0`) don't follow the spec-lowercase
  // convention, so a prefix built from spec would miss them entirely.
  const identityPrompts = useMemo(
    () =>
      allPrompts
        .filter(p => p.kind === AgentPromptKind.Identity && p.id === agent.id)
        .sort((a, b) => (b.updatedAt > a.updatedAt ? 1 : -1)),
    [allPrompts, agent.id]
  );

  // Shared templates (analysis / detection / summary-*) — same for every agent,
  // but we show them here so the admin can tune them alongside the identity.
  const templatePrompts = useMemo(
    () => allPrompts.filter(p => p.kind === AgentPromptKind.Template),
    [allPrompts]
  );

  const [selectedPromptKey, setSelectedPromptKey] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedPromptKey && identityPrompts[0]) {
      setSelectedPromptKey(`${identityPrompts[0].kind}:${identityPrompts[0].id}:${identityPrompts[0].version}`);
    }
  }, [identityPrompts, selectedPromptKey]);

  const current =
    allPrompts.find(p => `${p.kind}:${p.id}:${p.version}` === selectedPromptKey) ?? null;

  const [draftBody, setDraftBody] = useState('');
  const [draftName, setDraftName] = useState('');
  useEffect(() => {
    setDraftBody(current?.body ?? '');
    setDraftName(current?.displayName ?? '');
  }, [current]);

  const [savePrompt, saveState] = useSaveAgentPrompt(client);
  const [forkPrompt, forkState] = useForkAgentPrompt(client);
  const [deletePrompt] = useDeleteAgentPrompt(client);
  const confirm = useConfirm();

  const saveAsNew = async () => {
    if (!current) return;
    const res = await savePrompt({
      variables: {
        input: {
          kind: current.kind,
          id: current.id,
          body: draftBody,
          displayName: draftName || undefined,
          parentVersion: current.version,
          origin: 'manual'
        }
      }
    });
    const saved = res.data?.saveAgentPrompt;
    if (saved) setSelectedPromptKey(`${saved.kind}:${saved.id}:${saved.version}`);
  };

  const overwriteCurrent = async () => {
    if (!current) return;
    await savePrompt({
      variables: {
        input: {
          kind: current.kind,
          id: current.id,
          body: draftBody,
          version: current.version, // overwrite in place
          displayName: draftName || undefined,
          origin: current.origin ?? 'manual'
        }
      }
    });
  };

  const fork = async () => {
    if (!current) return;
    const res = await forkPrompt({
      variables: {kind: current.kind, id: current.id, fromVersion: current.version}
    });
    const forked = res.data?.forkAgentPrompt;
    if (forked) setSelectedPromptKey(`${forked.kind}:${forked.id}:${forked.version}`);
  };

  const onDelete = async () => {
    if (!current) return;
    const ok = await confirm({
      title: 'Delete prompt version?',
      message: `${current.kind}:${current.id} v${current.version} will be permanently removed. This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    await deletePrompt({
      variables: {kind: current.kind, id: current.id, version: current.version}
    });
    setSelectedPromptKey(null);
  };

  const isDirty =
    current && (draftBody !== current.body || (draftName || '') !== (current.displayName ?? ''));

  const handleEvolve = async () => {
    if (!current) return;
    setEvolveBusy(true);
    setEvolveMessage(null);
    try {
      const res = await evolvePrompt({
        variables: {
          input: {
            agentSpec: agent.specialization,
            agentVersion: agent.version,
            basePromptVersion: current.version,
            coachModel,
            sampleLimit: coachSample,
            autoEvalAfter: autoEvalEnabled,
            autoEvalModel: autoEvalEnabled ? autoEvalModel : null
          }
        }
      });
      const jobId = res.data?.evolveAgentPrompt?.jobId ?? null;
      setEvolveMessage({
        severity: 'success',
        text: jobId
          ? `Evolution queued as job #${jobId}. The new version will appear in this list when ready.`
          : 'Evolution queued. The new version will appear in this list when ready.',
        jobId,
      });
    } catch (e) {
      setEvolveMessage({severity: 'error', text: e instanceof Error ? e.message : String(e)});
    } finally {
      setEvolveBusy(false);
    }
  };

  return (
    <Box sx={{display: 'grid', gridTemplateColumns: '320px 1fr', gap: 2}}>
      <Paper sx={{p: 1.5, maxHeight: 640, overflow: 'auto'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
          <Typography variant="subtitle2" sx={{flex: 1}}>
            Identity prompts
          </Typography>
          <Tooltip title="Evolve selected prompt with a coach model (AI rewrites it based on eval failures)">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!current}
                onClick={() => setEvolveOpen(true)}
                sx={{textTransform: 'none'}}
              >
                Evolve
              </Button>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={() => refetch()}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        {loading && <CircularProgress size={20} />}
        <List dense disablePadding>
          {identityPrompts.map(p => {
            const key = `${p.kind}:${p.id}:${p.version}`;
            return (
              <ListItemButton
                key={key}
                selected={selectedPromptKey === key}
                onClick={() => setSelectedPromptKey(key)}
              >
                <ListItemText
                  primary={`v${p.version}`}
                  secondary={
                    <>
                      {p.displayName || p.origin || 'manual'}
                      {p.lastEvalScore != null && ` · ${formatScore(p.lastEvalScore)}`}
                    </>
                  }
                />
                {p.origin === 'forked' && <Chip label="forked" size="small" />}
              </ListItemButton>
            );
          })}
        </List>

        <Divider sx={{my: 2}} />

        <Typography variant="subtitle2" sx={{mb: 1}}>
          Shared templates
        </Typography>
        <List dense disablePadding>
          {templatePrompts.map(p => {
            const key = `${p.kind}:${p.id}:${p.version}`;
            return (
              <ListItemButton
                key={key}
                selected={selectedPromptKey === key}
                onClick={() => setSelectedPromptKey(key)}
              >
                <ListItemText primary={p.id} secondary={`v${p.version}`} />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>

      <Paper sx={{p: 2}}>
        {!current ? (
          <Typography variant="body2" color="text.secondary">
            Select a prompt on the left to view or edit.
          </Typography>
        ) : (
          <>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap'}}>
              <Chip size="small" color="primary" label={current.kind} />
              <Typography variant="subtitle2" sx={{fontFamily: 'monospace'}}>
                {current.id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                v{current.version}
                {current.parentVersion && ` (from v${current.parentVersion})`}
              </Typography>
              <Box sx={{flex: 1}} />
              <Tooltip title="Fork this prompt into a new version (parent = current)">
                <span>
                  <Button
                    size="small"
                    startIcon={<ForkRightIcon />}
                    onClick={fork}
                    disabled={forkState.loading}
                  >
                    Fork
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Overwrite this version in place (edits history)">
                <span>
                  <Button
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={overwriteCurrent}
                    disabled={!isDirty || saveState.loading}
                  >
                    Overwrite
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Save a new version (bumps patch), parent = current">
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={saveAsNew}
                    disabled={!isDirty || saveState.loading}
                  >
                    Save as new
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Delete this version">
                <span>
                  <IconButton size="small" color="error" onClick={onDelete}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <TextField
              label="Display name"
              size="small"
              fullWidth
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              sx={{mb: 2}}
            />

            <TextField
              label="Prompt body"
              fullWidth
              multiline
              minRows={18}
              maxRows={36}
              value={draftBody}
              onChange={e => setDraftBody(e.target.value)}
              inputProps={{style: {fontFamily: 'monospace', fontSize: 13}}}
            />

            {isDirty && (
              <Typography variant="caption" color="warning.main" sx={{mt: 1, display: 'block'}}>
                Unsaved changes. "Save as new" keeps history; "Overwrite" mutates this version.
              </Typography>
            )}
          </>
        )}
      </Paper>

      <EvolvePromptDialog
        open={evolveOpen}
        onClose={() => {
          setEvolveOpen(false);
          setEvolveMessage(null);
        }}
        currentVersion={current?.version ?? null}
        models={availableModels}
        coachModel={coachModel}
        onCoachModel={setCoachModel}
        sample={coachSample}
        onSample={setCoachSample}
        autoEvalModel={autoEvalModel}
        onAutoEvalModel={setAutoEvalModel}
        autoEvalEnabled={autoEvalEnabled}
        onAutoEvalEnabled={setAutoEvalEnabled}
        busy={evolveBusy}
        message={evolveMessage}
        onSubmit={handleEvolve}
      />
    </Box>
  );
}

function EvolvePromptDialog(props: {
  open: boolean;
  onClose: () => void;
  currentVersion: string | null;
  models: OpenRouterModelEntry[];
  coachModel: string;
  onCoachModel: (m: string) => void;
  sample: number;
  onSample: (n: number) => void;
  autoEvalModel: string;
  onAutoEvalModel: (m: string) => void;
  autoEvalEnabled: boolean;
  onAutoEvalEnabled: (b: boolean) => void;
  busy: boolean;
  message: {severity: 'success' | 'error'; text: string; jobId?: string | null} | null;
  onSubmit: () => void;
}) {
  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose} PaperProps={{sx: {width: {xs: '100%', sm: 520}, p: 2.5}}}>
      <Typography sx={{fontSize: 16, fontWeight: 700, color: pcolors.textPrimary}}>Evolve prompt</Typography>
      <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 0.5}}>
        Hand the current prompt + the most recent eval's failure cases to a coach model and
        save the rewrite as a new forked version. This runs as a background job — you can
        close this drawer and check progress on the Background Jobs page.
      </Typography>

      <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
        <Typography sx={sectionLabelSx}>Base version</Typography>
        <Typography sx={{fontFamily: 'monospace', fontSize: 13}}>{props.currentVersion || 'none selected'}</Typography>

        <ModelPicker
          models={props.models}
          value={props.coachModel}
          onChange={props.onCoachModel}
          label="Coach model"
          placeholder="Search strong reasoning models…"
        />

        <TextField
          size="small"
          label="Max failure cases to show coach"
          type="number"
          value={props.sample}
          onChange={e => props.onSample(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
        />

        <Divider sx={{my: 1}} />
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Checkbox
            checked={props.autoEvalEnabled}
            onChange={e => props.onAutoEvalEnabled(e.target.checked)}
            size="small"
            sx={{p: 0}}
          />
          <Typography sx={{fontSize: 13}}>Automatically eval the new prompt after creation</Typography>
        </Box>
        {props.autoEvalEnabled && (
          <ModelPicker
            models={props.models}
            value={props.autoEvalModel}
            onChange={props.onAutoEvalModel}
            label="Follow-up eval model"
            placeholder="Search cheap eval models…"
          />
        )}
      </Box>

      {props.message && (
        <Alert severity={props.message.severity} sx={{mt: 2, fontSize: 12}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <span>{props.message.text}</span>
            {props.message.jobId && (
              <a
                href={`#/admin/operations/tasks?jobId=${props.message.jobId}`}
                style={{color: 'inherit', textDecoration: 'underline', fontWeight: 600}}
              >
                View job →
              </a>
            )}
          </Box>
        </Alert>
      )}

      <Box sx={{mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
        <Button size="small" onClick={props.onClose} disabled={props.busy}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          onClick={props.onSubmit}
          disabled={props.busy || !props.currentVersion}
        >
          {props.busy ? 'Queuing…' : 'Evolve'}
        </Button>
      </Box>
    </Drawer>
  );
}

// ----------------------------------------------------------------
// Evals tab — prompt × model matrix + recent reports
// ----------------------------------------------------------------
function EvalsTab({agent, client}: {agent: EvalAgentEntry; client: Props['client']}) {
  const notify = useNotify();
  const {data: promptsData} = useListAgentPrompts(client);
  const {data: modelsData} = useListOpenRouterModels(client);
  const {data: reportsData, refetch: refetchReports} = useGetEvalReportsForAgent(client, {
    agentSpec: agent.specialization,
    agentVersion: agent.version,
    limit: 500
  });
  const {data: testCasesData} = useGetEvalTestCases(client, 500);

  const reports: EvalReport[] = reportsData?.getEvalReportsForAgent ?? [];
  const promptVersions: string[] = useMemo(() => {
    const identity = (promptsData?.listAgentPrompts ?? [])
      .filter(p => p.kind === AgentPromptKind.Identity && p.id === agent.id)
      .map(p => p.version);
    // Include "latest" as a shortcut.
    return ['latest', ...identity];
  }, [promptsData, agent.id]);

  const availableModels: OpenRouterModelEntry[] = modelsData?.listOpenRouterModels ?? [];
  // Matrix model picks are scoped per (agent, version) so switching focus
  // doesn't overwrite the list you curated for another agent.
  const matrixKey = `agentsLab.evals.matrixModels:${agent.specialization}:${agent.version}`;
  const sampleKey = `agentsLab.evals.sampleLimit:${agent.specialization}:${agent.version}`;
  const [matrixModels, setMatrixModels] = usePersistedState<string[]>(
    matrixKey,
    STARRED_MODELS.slice(0, 4),
  );
  const [sampleLimit, setSampleLimit] = usePersistedState<number>(sampleKey, 20);
  const [runEval] = useRunEval(client);
  const [runMulti] = useRunMultiModelEval(client);
  const [openReport, setOpenReport] = useState<EvalReport | null>(null);

  // Poll BullMQ for in-flight eval jobs so the matrix can show a spinner
  // on the cell being scored. The hook already polls every 5s, so we get
  // live progress without any extra plumbing.
  const {data: jobsData} = useListBullmqJobs(client, {
    states: [BullmqJobState.Waiting, BullmqJobState.Active, BullmqJobState.Delayed],
    limit: 50
  });
  const activeCellIndex = useMemo(() => {
    type Progress = {fractionComplete?: number; stage?: string; current?: number; total?: number; message?: string} | null;
    const map = new Map<string, {state: BullmqJobState; progress: Progress}>();
    for (const j of jobsData?.listBullmqJobs ?? []) {
      let data: {agentSpec?: string; agentVersion?: string; promptVersion?: string | null; modelName?: string | null; modelNames?: string[]} = {};
      try {
        data = JSON.parse(j.dataJson);
      } catch {
        continue;
      }
      if (data.agentSpec !== agent.specialization || data.agentVersion !== agent.version) continue;
      let progress: Progress = null;
      if (j.progressJson) {
        try {
          progress = JSON.parse(j.progressJson) as Progress;
        } catch {
          /* ignore */
        }
      }
      const prompt = data.promptVersion || 'latest';
      const models = data.modelNames || (data.modelName ? [data.modelName] : []);
      for (const m of models) {
        map.set(`${prompt}|${m}`, {state: j.state, progress});
      }
    }
    return map;
  }, [jobsData, agent.specialization, agent.version]);
  const isCellActive = (pv: string, m: string) => {
    return activeCellIndex.get(`${pv}|${m}`) || null;
  };

  // Map (promptVersion|model) → latest report for that combo.
  const cellIndex = useMemo(() => {
    const map = new Map<string, EvalReport>();
    for (const r of reports) {
      const key = `${r.promptVersion}|${r.model}`;
      const prev = map.get(key);
      if (!prev || r.ranAt > prev.ranAt) map.set(key, r);
    }
    return map;
  }, [reports]);

  // Prompt version-strings as stored on EvalReport are compound:
  // "identity:<v>|template:<v>". We normalize by taking just the identity
  // slice for matching against the sidebar's prompt list.
  const extractIdentity = (v: string): string => {
    const m = v.match(/identity:([^|]+)/);
    return m ? m[1] : v;
  };

  const resolveCell = (promptVersion: string, model: string): EvalReport | undefined => {
    for (const [key, r] of cellIndex.entries()) {
      if (extractIdentity(key.split('|')[0]) === promptVersion && key.endsWith(`|${model}`)) {
        return r;
      }
    }
    // Try 'latest' → any identity
    if (promptVersion === 'latest') {
      const latestReports = reports.filter(r => r.model === model);
      if (latestReports.length > 0) return latestReports[0];
    }
    return undefined;
  };

  const runCell = async (promptVersion: string, model: string) => {
    try {
      const res = await runEval({
        variables: {
          input: {
            agentSpec: agent.specialization,
            agentVersion: agent.version,
            promptVersion: promptVersion === 'latest' ? null : promptVersion,
            modelName: model,
            sampleLimit
          }
        }
      });
      const jobId = res.data?.runEval?.jobId;
      notify({
        severity: 'success',
        message: jobId ? `Eval queued as job #${jobId} (${model}).` : `Eval queued (${model}).`,
        action: jobId ? {label: 'View job', href: `#/admin/operations/tasks?jobId=${jobId}`} : undefined,
        durationMs: 8000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Eval failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const runRow = async (promptVersion: string) => {
    try {
      const res = await runMulti({
        variables: {
          input: {
            agentSpec: agent.specialization,
            agentVersion: agent.version,
            promptVersion: promptVersion === 'latest' ? null : promptVersion,
            modelNames: matrixModels,
            sampleLimit
          }
        }
      });
      const jobId = res.data?.runMultiModelEval?.jobId;
      notify({
        severity: 'success',
        message: jobId
          ? `Multi-model eval queued as job #${jobId} (${matrixModels.length} model${matrixModels.length === 1 ? '' : 's'}).`
          : `Multi-model eval queued (${matrixModels.length} model${matrixModels.length === 1 ? '' : 's'}).`,
        action: jobId ? {label: 'View job', href: `#/admin/operations/tasks?jobId=${jobId}`} : undefined,
        durationMs: 8000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Multi-model eval failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      <Paper sx={{...cardSx}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, p: 2, flexWrap: 'wrap', borderBottom: `1px solid ${pcolors.border}`}}>
          <Typography sx={{fontSize: 15, fontWeight: 600, color: pcolors.textPrimary, flex: 1}}>
            Prompt × Model
          </Typography>
          <Box sx={{minWidth: 420, maxWidth: 560, flex: 1}}>
            <ModelPicker
              multiple
              models={availableModels}
              value={matrixModels}
              onChange={setMatrixModels}
              label={`Models (${matrixModels.length})`}
              placeholder="Search OpenRouter catalogue…"
            />
          </Box>
          <TextField
            size="small"
            label="Sample"
            type="number"
            value={sampleLimit}
            onChange={e => setSampleLimit(Math.max(1, Math.min(500, Number(e.target.value) || 0)))}
            sx={{width: 96}}
          />
          <Typography sx={{fontSize: 11, color: pcolors.textMuted}}>
            {testCasesData?.getEvalTestCases?.length ?? 0} test cases
          </Typography>
          <IconButton size="small" onClick={() => refetchReports()} sx={{color: pcolors.textMuted}}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>

        <TableContainer sx={{overflow: 'auto'}}>
          <Table size="small" sx={{tableLayout: 'fixed', minWidth: 220 + matrixModels.length * 88 + 96}}>
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={{
                  ...tableHeadCellSx,
                  position: 'sticky',
                  left: 0,
                  bgcolor: pcolors.headerBg,
                  zIndex: 2,
                  width: 220,
                  minWidth: 220,
                  borderRight: `1px solid ${pcolors.border}`
                }}>
                  Prompt version
                </TableCell>
                {matrixModels.map(m => (
                  <TableCell key={m} align="center" sx={{...tableHeadCellSx, width: 88, minWidth: 88, px: 0.5, py: 1.5, verticalAlign: 'bottom'}}>
                    <Tooltip title={m}>
                      <Typography sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: pcolors.textSecondary,
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {m.split('/').slice(-1)[0]}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell align="center" sx={{...tableHeadCellSx, width: 96, minWidth: 96}}>
                  Row
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {promptVersions.map(pv => (
                <TableRow key={pv} hover>
                  <TableCell sx={{
                    ...tableBodyCellSx,
                    fontWeight: 500,
                    color: pcolors.textPrimary,
                    fontFamily: 'monospace',
                    position: 'sticky',
                    left: 0,
                    bgcolor: pcolors.card,
                    zIndex: 1,
                    borderRight: `1px solid ${pcolors.border}`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {pv}
                  </TableCell>
                  {matrixModels.map(m => {
                    const report = resolveCell(pv, m);
                    const active = isCellActive(pv, m);
                    if (active) {
                      const pct = Math.round((active.progress?.fractionComplete ?? 0) * 100);
                      const stage = active.progress?.stage || (active.state === BullmqJobState.Waiting ? 'queued' : 'running');
                      const tooltipMsg = active.progress?.message
                        ? `${stage} · ${active.progress.message}`
                        : `${stage} (${active.state.toLowerCase()})`;
                      return (
                        <TableCell key={m} align="center" sx={{px: 1}}>
                          <Tooltip title={tooltipMsg}>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.25,
                                minWidth: 56,
                              }}
                            >
                              <CircularProgress
                                variant={pct > 0 ? 'determinate' : 'indeterminate'}
                                value={pct}
                                size={20}
                                thickness={5}
                                sx={{color: pcolors.blue[500]}}
                              />
                              <Typography sx={{fontSize: 10, color: pcolors.textMuted, fontVariantNumeric: 'tabular-nums'}}>
                                {pct > 0 ? `${pct}%` : stage}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={m} align="center" sx={{px: 1}}>
                        {report ? (
                          <Tooltip title={`${report.passedCases}/${report.totalCases} passed · $${report.totalCost.toFixed(4)} · click for details`}>
                            <Box
                              onClick={() => setOpenReport(report)}
                              sx={scoreBadgeSx(report.overallScore, ScoreDirection.HigherIsBetter)}
                            >
                              {Math.round((report.overallScore || 0) * 100)}%
                            </Box>
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Run eval: ${pv} × ${m.split('/').slice(-1)[0]}`}>
                            <Box
                              onClick={() => runCell(pv, m)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                cursor: 'pointer',
                                color: pcolors.textDisabled,
                                transition: 'all 0.15s',
                                '&:hover': {bgcolor: pcolors.borderLight, color: pcolors.blue[500]}
                              }}
                            >
                              <AddIcon sx={{fontSize: 16}} />
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Tooltip title={`Run all ${matrixModels.length} models for this prompt`}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => runRow(pv)}
                        sx={{fontSize: 12, py: 0.25, minWidth: 0}}
                      >
                        Run row
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {promptVersions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={matrixModels.length + 2}>
                    <Box sx={{textAlign: 'center', py: 5, color: pcolors.textMuted}}>
                      <Typography sx={{fontSize: 13}}>
                        No prompt versions yet. Seed them from the devconsole ("Agent prompts → Firestore")
                        or save a new one from the Prompts tab.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{...cardSx, p: 2}}>
        <Typography sx={{fontSize: 15, fontWeight: 600, color: pcolors.textPrimary, mb: 1.5}}>
          Recent eval runs ({reports.length})
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={tableHeadCellSx}>Prompt</TableCell>
                <TableCell sx={tableHeadCellSx}>Model</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Score</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Passed</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Cost</TableCell>
                <TableCell sx={tableHeadCellSx}>Ran</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.slice(0, 30).map(r => {
                const {bg, color} = scoreColors(r.overallScore, ScoreDirection.HigherIsBetter);
                return (
                  <TableRow key={r.id} hover sx={{cursor: 'pointer'}} onClick={() => setOpenReport(r)}>
                    <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 12, color: pcolors.textPrimary}}>
                      {extractIdentity(r.promptVersion)}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 12, color: pcolors.textSecondary}}>
                      {r.model}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, bgcolor: bg, color, fontSize: 12, fontWeight: 700}}>
                        {formatScore(r.overallScore)}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={tableBodyCellSx}>
                      {r.passedCases}/{r.totalCases}
                    </TableCell>
                    <TableCell align="right" sx={{...tableBodyCellSx, fontFamily: 'monospace', color: pcolors.textSecondary}}>
                      ${r.totalCost.toFixed(4)}
                    </TableCell>
                    <TableCell sx={{fontSize: 11, color: pcolors.textMuted}}>
                      {new Date(r.ranAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                );
              })}
              {reports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{textAlign: 'center', py: 4, color: pcolors.textMuted}}>
                      <Typography sx={{fontSize: 13}}>
                        No runs yet. Click any cell in the matrix above to queue one.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <EvalReportDrawer
        client={client}
        report={openReport}
        onClose={() => setOpenReport(null)}
        onRerun={(r) => runCell(extractIdentity(r.promptVersion), r.model)}
      />
    </Box>
  );
}

// ----------------------------------------------------------------
// Report detail drawer — per-case predicted vs expected, with errors.
// ----------------------------------------------------------------
interface EvalCaseResult {
  testCaseId: string;
  videoId: string;
  predicted: {score: number; rating: string; explanation: string};
  expected: {score?: number | null; rating?: string | null; restricted?: boolean | null};
  caseScore: number;
  error?: string;
  model?: string;
  promptVersion?: string;
  tokenUsage?: {input: number; output: number; total: number};
  costEstimate?: {totalCost: number} | null;
}

function EvalReportDrawer({
  client,
  report,
  onClose,
  onRerun,
}: {
  client: Props['client'];
  report: EvalReport | null;
  onClose: () => void;
  onRerun: (r: EvalReport) => void;
}) {
  // The matrix list query is intentionally slim — it fetches only the
  // aggregate columns shown in score cells, not per-case results. Refetch
  // the full report on drawer open so `fieldAccuracy` and `results` are
  // populated. Skips until a report is selected.
  const {data: fullData, loading: fullLoading} = useGetEvalReport(
    client,
    report?.id ?? null,
  );
  // Prefer the hydrated doc; fall back to the list-projection while it loads.
  const full = (fullData?.getEvalReport as EvalReport | null | undefined) ?? report;
  const cases = (full?.results as unknown as EvalCaseResult[] | undefined) ?? [];

  return (
    <Drawer
      anchor="right"
      open={!!report}
      onClose={onClose}
      PaperProps={{sx: {width: {xs: '100%', sm: 640}}}}
    >
      {full && (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Box sx={{p: 2.5, borderBottom: `1px solid ${pcolors.border}`, display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Typography sx={{fontSize: 11, color: pcolors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4}}>
                Eval run
              </Typography>
              <Typography sx={{fontSize: 16, fontWeight: 700, mt: 0.25, color: pcolors.textPrimary}}>
                {full.model}
              </Typography>
              <Typography sx={{fontSize: 11, color: pcolors.textMuted, fontFamily: 'monospace', mt: 0.25}}>
                {full.promptVersion}
              </Typography>
              <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 0.25}}>
                Ran {new Date(full.ranAt).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={scoreBadgeSx(full.overallScore, ScoreDirection.HigherIsBetter)}>{formatScore(full.overallScore)}</Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{px: 2.5, py: 1.5, borderBottom: `1px solid ${pcolors.borderLight}`, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2}}>
            <Box>
              <Typography sx={sectionLabelSx}>Passed</Typography>
              <Typography sx={{fontSize: 16, fontWeight: 700, mt: 0.25}}>{full.passedCases}/{full.totalCases}</Typography>
            </Box>
            <Box>
              <Typography sx={sectionLabelSx}>Rating</Typography>
              <Typography sx={{fontSize: 16, fontWeight: 700, mt: 0.25}}>
                {fullLoading && !fullData
                  ? '…'
                  : `${full.fieldAccuracy?.rating?.correct ?? 0}/${full.fieldAccuracy?.rating?.total ?? 0}`}
              </Typography>
            </Box>
            <Box>
              <Typography sx={sectionLabelSx}>Tokens</Typography>
              <Typography sx={{fontSize: 16, fontWeight: 700, mt: 0.25, fontVariantNumeric: 'tabular-nums'}}>
                {full.totalTokenUsage?.total?.toLocaleString() ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography sx={sectionLabelSx}>Cost</Typography>
              <Typography sx={{fontSize: 16, fontWeight: 700, mt: 0.25, fontFamily: 'monospace'}}>
                ${full.totalCost.toFixed(4)}
              </Typography>
            </Box>
          </Box>

          {full.error && (
            <Alert severity="warning" sx={{m: 2}}>
              {full.error}
            </Alert>
          )}

          <Box sx={{flex: 1, overflow: 'auto'}}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={tableHeadCellSx}>Video</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'center'}}>Expected</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'center'}}>Predicted</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'center'}}>Case</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box sx={{textAlign: 'center', py: 4, color: pcolors.textMuted}}>
                        {fullLoading ? (
                          <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
                            <CircularProgress size={14} />
                            <Typography sx={{fontSize: 13}}>Loading per-case results…</Typography>
                          </Box>
                        ) : (
                          <Typography sx={{fontSize: 13}}>No per-case results in this report.</Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {cases.map((c) => {
                  const pass = c.caseScore >= 0.7;
                  const ratingOk = c.expected?.rating === c.predicted?.rating;
                  const hasError = !!c.error;
                  return (
                    <TableRow key={c.testCaseId} sx={hasError ? {bgcolor: pcolors.red[50]} : undefined}>
                      <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 11, color: pcolors.textSecondary, verticalAlign: 'top'}}>
                        {c.videoId}
                        {hasError && (
                          <Typography sx={{fontSize: 11, color: pcolors.red[700], mt: 0.5, fontFamily: 'inherit', whiteSpace: 'pre-wrap'}}>
                            ⚠ {c.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{...tableBodyCellSx, textAlign: 'center', verticalAlign: 'top'}}>
                        <Typography sx={{fontSize: 12, fontVariantNumeric: 'tabular-nums', fontWeight: 600}}>
                          {c.expected?.score ?? '—'}
                        </Typography>
                        <Typography sx={{fontSize: 10, color: pcolors.textMuted}}>
                          {c.expected?.rating || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{...tableBodyCellSx, textAlign: 'center', verticalAlign: 'top'}}>
                        {hasError ? (
                          <Typography sx={{fontSize: 11, color: pcolors.textMuted, fontStyle: 'italic'}}>errored</Typography>
                        ) : (
                          <>
                            <Typography sx={{fontSize: 12, fontVariantNumeric: 'tabular-nums', fontWeight: 600}}>
                              {c.predicted?.score ?? '—'}
                            </Typography>
                            <Typography sx={{fontSize: 10, color: ratingOk ? pcolors.green[700] : pcolors.red[700]}}>
                              {c.predicted?.rating || '—'}
                            </Typography>
                          </>
                        )}
                      </TableCell>
                      <TableCell sx={{textAlign: 'center', verticalAlign: 'top'}}>
                        {hasError ? (
                          <CancelIcon sx={{fontSize: 16, color: pcolors.red[600]}} />
                        ) : pass ? (
                          <CheckCircleIcon sx={{fontSize: 16, color: pcolors.green[600]}} />
                        ) : (
                          <CancelIcon sx={{fontSize: 16, color: pcolors.red[600]}} />
                        )}
                        <Typography sx={{fontSize: 10, color: pcolors.textMuted, mt: 0.25}}>
                          {(c.caseScore * 100).toFixed(0)}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{p: 2, borderTop: `1px solid ${pcolors.border}`, display: 'flex', gap: 1}}>
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => {
                if (!full) return;
                onRerun(full);
                onClose();
              }}
            >
              Re-run
            </Button>
            <Box sx={{flex: 1}} />
            <Typography sx={{fontSize: 11, color: pcolors.textMuted, alignSelf: 'center'}}>
              report id: {full.id}
            </Typography>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

// ----------------------------------------------------------------
// Test Cases tab — browsable table with a per-agent coverage matrix
// ----------------------------------------------------------------
function TestCasesTab({agents, client}: {agents: EvalAgentEntry[]; client: Props['client']}) {
  const {data, loading, refetch} = useGetEvalTestCases(client, 500);
  const [seedTestCases, seedState] = useSeedEvalTestCases(client);
  const [seedCount, setSeedCount] = useState(50);
  const notify = useNotify();
  const testCases = data?.getEvalTestCases ?? [];

  // Per-agent coverage: how many test cases have an expected entry for each
  // specialization. Helps the admin spot "no expected data for this agent"
  // before hitting Run and getting an empty report.
  const coverage = useMemo(() => {
    const out: Record<string, number> = {};
    for (const tc of testCases) {
      const expected = (tc.expected || {}) as Record<string, unknown>;
      for (const key of Object.keys(expected)) {
        out[key] = (out[key] || 0) + 1;
      }
    }
    return out;
  }, [testCases]);

  const handleSeed = async () => {
    const res = await seedTestCases({variables: {count: seedCount}});
    const r = res.data?.seedEvalTestCasesFromScores;
    refetch();
    if (r) {
      notify({
        severity: r.created > 0 ? 'success' : 'info',
        message: `Seeded ${r.created} test case${r.created === 1 ? '' : 's'}${
          r.skipped > 0 ? ` (${r.skipped} already existed)` : ''
        }.`,
      });
    }
  };

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        <Typography variant="h6">Test cases ({testCases.length})</Typography>
        <Box sx={{flex: 1}} />
        <TextField
          size="small"
          type="number"
          label="Count"
          value={seedCount}
          onChange={e => setSeedCount(Math.max(1, Math.min(500, Number(e.target.value) || 50)))}
          sx={{width: 100}}
        />
        <Button variant="contained" onClick={handleSeed} disabled={seedState.loading}>
          {seedState.loading ? 'Seeding…' : 'Seed from feedback + scores'}
        </Button>
        <IconButton onClick={() => refetch()}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Coverage chips per agent */}
      <Paper sx={{p: 2, mb: 2}}>
        <Typography variant="overline" color="text.secondary">
          Expected-output coverage by agent
        </Typography>
        <Box sx={{mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap'}}>
          {agents.map(a => {
            const n = coverage[String(a.specialization)] || 0;
            return (
              <Chip
                key={`${a.specialization}|${a.version}`}
                avatar={<AgentAvatar agent={a} size={24} />}
                label={`${a.name}: ${n} case${n === 1 ? '' : 's'}`}
                color={n > 0 ? 'primary' : 'default'}
                variant={n > 0 ? 'filled' : 'outlined'}
              />
            );
          })}
        </Box>
      </Paper>

      {loading && testCases.length === 0 && <CircularProgress />}
      {!loading && testCases.length === 0 && (
        <Alert severity="info">
          No test cases yet. Click <strong>Seed from feedback + scores</strong> to bootstrap a ground-truth
          set from your existing user feedback (authoritative) and production agent scores (fallback).
        </Alert>
      )}

      {testCases.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Video ID</TableCell>
                <TableCell>Agents with expected</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Added</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testCases.map(tc => {
                const keys = Object.keys((tc.expected || {}) as Record<string, unknown>);
                return (
                  <TableRow key={tc.id} hover>
                    <TableCell sx={{fontFamily: 'monospace'}}>{tc.videoId}</TableCell>
                    <TableCell>
                      <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                        {keys.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            (empty)
                          </Typography>
                        )}
                        {keys.map(k => (
                          <Chip key={k} size="small" label={shortSpecialization(k)} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{tc.note || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(tc.createdAt).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Page
// ----------------------------------------------------------------
export default function AdminAgentsLab({client}: Props) {
  usePageTitle('Admin – Agents');
  const {data: agentsData, loading} = useListEvalAgents(client);
  const agents: EvalAgentEntry[] = agentsData?.listEvalAgents ?? [];
  const [selectedAgentKey, setSelectedAgentKey] = usePersistedState<string>(
    'agentsLab.selectedAgentKey',
    '',
  );
  useEffect(() => {
    // Seed to the first agent only when persisted value is missing OR points
    // at an agent that no longer exists (e.g. version bump since last visit).
    if (agents.length === 0) return;
    const stillValid = agents.some(a => `${a.specialization}|${a.version}` === selectedAgentKey);
    if (!selectedAgentKey || !stillValid) {
      setSelectedAgentKey(`${agents[0].specialization}|${agents[0].version}`);
    }
  }, [agents, selectedAgentKey, setSelectedAgentKey]);
  const agent = useMemo(
    () => agents.find(a => `${a.specialization}|${a.version}` === selectedAgentKey),
    [agents, selectedAgentKey]
  );

  const [tab, setTab] = usePersistedState<'overview' | 'prompts' | 'evals' | 'testcases'>(
    'agentsLab.tab',
    'evals',
  );
  const [seedTestCases, seedState] = useSeedEvalTestCases(client);
  const {data: tcData} = useGetEvalTestCases(client, 500);
  const testCaseCount = tcData?.getEvalTestCases?.length ?? 0;

  return (
    <Box sx={{bgcolor: pcolors.bg, minHeight: '100vh'}}>
      <Container maxWidth={false} sx={pageSx}>
        {/* Page header */}
        <Box sx={{mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
          <Box>
            <Typography sx={pageTitleSx}>Agents</Typography>
            <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.25}}>
              Browse agents, edit their prompts, and evaluate prompt versions across models.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            disabled={seedState.loading}
            onClick={() => seedTestCases({variables: {count: 50}})}
            sx={{whiteSpace: 'nowrap'}}
          >
            {seedState.loading
              ? 'Seeding…'
              : testCaseCount === 0
                ? 'Seed test cases'
                : `${testCaseCount} test case${testCaseCount === 1 ? '' : 's'} · seed more`}
          </Button>
        </Box>

        {/* Sidebar + content */}
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '280px 1fr'}, gap: 2}}>
          <Paper sx={{...cardSx, p: 1, height: 'fit-content', position: 'sticky', top: 16}}>
            <Box sx={{px: 1.5, py: 1}}>
              <Typography sx={sectionLabelSx}>Agents · {agents.length}</Typography>
            </Box>
            {loading && <CircularProgress size={20} sx={{m: 2}} />}
            <Box>
              {agents.map(a => {
                const key = `${a.specialization}|${a.version}`;
                const isSelected = selectedAgentKey === key;
                return (
                  <Box
                    key={key}
                    onClick={() => setSelectedAgentKey(key)}
                    sx={listRowSx(isSelected)}
                  >
                    <AgentAvatar agent={a} size={36} />
                    <Box sx={{flex: 1, minWidth: 0}}>
                      <Typography sx={{fontSize: 13, fontWeight: 500, color: pcolors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {a.name}
                      </Typography>
                      <Typography sx={{fontSize: 11, color: pcolors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {humanizeAgentType(a.type)} · {shortSpecialization(a.specialization)} · v{a.version}
                      </Typography>
                    </Box>
                    <Tooltip title={`Needs ${humanizeDataRequirement(a.dataRequirement)}`}>
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: 0.3,
                          textTransform: 'uppercase',
                          flex: 'none',
                          bgcolor:
                            a.dataRequirement === 'TRANSCRIPT_REQUIRED'
                              ? pcolors.amber[50]
                              : a.dataRequirement === 'SUMMARY_REQUIRED'
                                ? pcolors.blue[50]
                                : pcolors.green[50],
                          color:
                            a.dataRequirement === 'TRANSCRIPT_REQUIRED'
                              ? pcolors.amber[700]
                              : a.dataRequirement === 'SUMMARY_REQUIRED'
                                ? pcolors.blue[700]
                                : pcolors.green[700],
                        }}
                      >
                        {a.dataRequirement === 'TRANSCRIPT_REQUIRED'
                          ? 'T'
                          : a.dataRequirement === 'SUMMARY_REQUIRED'
                            ? 'S'
                            : 'M'}
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </Paper>

          <Box sx={{minWidth: 0}}>
            {!agent ? (
              <Paper sx={{...cardSx, p: 5, textAlign: 'center'}}>
                <Typography sx={{color: pcolors.textMuted, fontSize: 14}}>
                  {loading ? 'Loading agents…' : 'No agents registered.'}
                </Typography>
              </Paper>
            ) : (
              <>
                <Tabs
                  value={tab}
                  onChange={(_e, v) => setTab(v)}
                  sx={{borderBottom: `1px solid ${pcolors.border}`, mb: 2}}
                >
                  <Tab value="overview" label="Overview" />
                  <Tab value="prompts" label="Prompts" />
                  <Tab value="evals" label="Evals" />
                  <Tab value="testcases" label="Test cases" />
                </Tabs>
                {tab === 'overview' && <OverviewTab agent={agent} client={client} />}
                {tab === 'prompts' && <PromptsTab agent={agent} client={client} />}
                {tab === 'evals' && <EvalsTab agent={agent} client={client} />}
                {tab === 'testcases' && <TestCasesTab agents={agents} client={client} />}
              </>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
